import type {
    SerializationResult,
    SerializedFeature,
} from "@alchemiesolns/feature-extractor";

/**
 * The three PIPH-60 relationship types between a functional group and the
 * primary feature(s) it belongs to.
 *
 *  - "at"      — the group connects to a single primary from outside
 *                (e.g. an alcohol on a carbon chain, a halide on a ring,
 *                a ketone whose carbonyl C is shared with a chain)
 *  - "in"      — the group is fully integrated inside a single primary
 *                (e.g. an ether oxygen that sits within a ring, an alkene
 *                whose double bond is between two chain carbons)
 *  - "between" — the group spans two primaries
 *                (e.g. a bridging ether O that connects a ring to a chain)
 */
export type FunctionalGroupRelationship = "at" | "in" | "between";

/**
 * Parents that a functional group "belongs to" in the PIPH-60 sense.
 * `kind` identifies the relationship; `parentIds` lists one or two
 * primary feature IDs depending on the kind.
 */
export interface RelationshipInfo {
    kind: FunctionalGroupRelationship;
    parentIds: string[];
}

/**
 * Classify a functional group's relationship to its primary parent(s).
 *
 * Algorithm:
 *  1. Determine which "primary" features (rings, ring systems, chains,
 *     bridged bicyclics, atom clusters) each of the functional group's
 *     heavy atoms (non-H) belongs to.
 *  2. If every heavy atom lies inside some primary, the group is "in"
 *     that primary. (Hydrogen atoms bonded to primary atoms are ignored.)
 *  3. Otherwise, walk the bonds of the group's non-primary atoms to find
 *     which primaries the group is adjacent to. If two or more distinct
 *     primaries are touched, the group is "between"; otherwise "at".
 *
 * PIPH-106: Hydrogen atoms are now excluded from the "outside" check.
 * A secondary amine in a ring has atomIndices [N, H] where N is in the ring
 * and H is bonded to N. The H being "outside" shouldn't change the
 * classification from "in" to "at".
 */
export function classifyRelationship(
    feature: SerializedFeature,
    result: SerializationResult,
): RelationshipInfo {
    const primaries = result.features.filter(f =>
        f.type === "ring" ||
        f.type === "chain" ||
        f.type === "bridged_bicyclic" ||
        f.type === "atom_cluster",
    );

    // Pre-index primary atom membership for fast lookup.
    const primaryIdByAtom = new Map<number, string[]>();
    for (const p of primaries) {
        for (const ai of p.atomIndices) {
            const list = primaryIdByAtom.get(ai);
            if (list) list.push(p.id);
            else primaryIdByAtom.set(ai, [p.id]);
        }
    }

    // Build a quick lookup for atom symbols
    const atomSymbolByIndex = new Map<number, string>();
    for (const atom of result.atoms) {
        atomSymbolByIndex.set(atom.index, atom.symbol);
    }

    // Partition the group's atoms into "inside a primary" vs "not".
    // PIPH-106: Only consider heavy atoms (non-H) for the "in" classification.
    const atomsInsidePrimary: number[] = [];
    const atomsOutsidePrimary: number[] = [];
    const hydrogenAtoms: number[] = [];
    const touchedByOverlap = new Set<string>();

    for (const ai of feature.atomIndices) {
        const symbol = atomSymbolByIndex.get(ai);
        // Track hydrogen atoms separately - they don't affect "in" classification
        if (symbol === "H") {
            hydrogenAtoms.push(ai);
            continue;
        }

        const owners = primaryIdByAtom.get(ai);
        if (owners && owners.length > 0) {
            atomsInsidePrimary.push(ai);
            for (const id of owners) touchedByOverlap.add(id);
        } else {
            atomsOutsidePrimary.push(ai);
        }
    }

    // Case 1: every heavy atom is inside some primary feature.
    // The group is fully integrated — "in" the primary that contains it.
    // (Hydrogen atoms bonded to these heavy atoms don't break "in" status.)
    if (atomsOutsidePrimary.length === 0 && touchedByOverlap.size > 0) {
        // If overlap touches exactly one primary, "in" that primary.
        // If overlap touches multiple primaries (e.g. an alkene spanning
        // two fused rings), the group effectively spans two features.
        if (touchedByOverlap.size === 1) {
            return {kind: "in", parentIds: [first(touchedByOverlap)]};
        }
        // PIPH-145: Return ALL touched primaries, not just first 2
        return {kind: "between", parentIds: Array.from(touchedByOverlap)};
    }

    // PIPH-316: shared-carbon carbonyl short-circuit. An ester/amide/etc. whose
    // carbonyl carbon is shared with ONE primary (it sits in that chain/ring) is
    // a SUBSTITUENT "at" that primary — even though its O-alkyl / N side reaches
    // other atoms. Without this, the adjacency walk below sees the alkyl/N side
    // touch a second feature and wrongly returns "between". Anhydride is excluded:
    // it genuinely bridges two primaries via TWO carbonyl carbons, so it must
    // keep flowing through the adjacency path.
    if (
        feature.internalName?.startsWith("carbonyl.") &&
        feature.internalName !== "carbonyl.acidAnhydride"
    ) {
        const carbonylPrimaries = new Set<string>();
        for (const ai of feature.atomIndices) {
            if (atomSymbolByIndex.get(ai) !== "C") continue;
            const atom = result.atoms.find(a => a.index === ai);
            if (!atom) continue;
            // A carbonyl carbon: double-bonded to an O that's also in this FG.
            const isCarbonyl = atom.bondIndices.some(bi => {
                const bond = result.bonds[bi];
                if (!bond || bond.order !== 2) return false;
                const other = bond.atomIndices[0] === ai ? bond.atomIndices[1] : bond.atomIndices[0];
                return atomSymbolByIndex.get(other) === "O" && feature.atomIndices.includes(other);
            });
            if (!isCarbonyl) continue;
            const owners = primaryIdByAtom.get(ai);
            if (owners) for (const id of owners) carbonylPrimaries.add(id);
        }
        // Exactly one primary owns the carbonyl carbon → it's "at" that primary.
        if (carbonylPrimaries.size === 1) {
            return {kind: "at", parentIds: [first(carbonylPrimaries)]};
        }
    }

    // Case 2: the group has heavy atoms that aren't in any primary. Walk bonds
    // from those atoms to find adjacent primaries.
    const touchedByAdjacency = new Set<string>(touchedByOverlap);
    for (const ai of atomsOutsidePrimary) {
        const atom = result.atoms.find(a => a.index === ai);
        if (!atom) continue;
        for (const bondIndex of atom.bondIndices) {
            const bond = result.bonds[bondIndex];
            if (!bond) continue;
            const otherIndex = bond.atomIndices[0] === ai
                ? bond.atomIndices[1]
                : bond.atomIndices[0];
            // Don't count bonds back into the functional group itself.
            if (feature.atomIndices.includes(otherIndex)) continue;
            const owners = primaryIdByAtom.get(otherIndex);
            if (owners) {
                for (const id of owners) touchedByAdjacency.add(id);
            }
        }
    }

    if (touchedByAdjacency.size >= 2) {
        // PIPH-145: Return ALL touched primaries, not just first 2
        return {kind: "between", parentIds: Array.from(touchedByAdjacency)};
    }
    if (touchedByAdjacency.size === 1) {
        return {kind: "at", parentIds: [first(touchedByAdjacency)]};
    }

    // Fallback — no primary parents at all. Treat as "at" with no parent.
    // This is unusual (a functional group floating free) but we don't crash.
    return {kind: "at", parentIds: []};
}

function first<T>(set: Set<T>): T {
    // Assumes non-empty; callers guard.
    return set.values().next().value as T;
}
